/**
 * Admin User Management Tab
 * View all registered users and their profile status
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, Search, ChevronLeft, ChevronRight, CheckCircle, AlertCircle,
  User, Mail, Calendar, MapPin, Cat, Dog, RefreshCw, Eye, Shield, ShieldOff
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";

import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../lib/api";

const AdminUsersTab = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [roleChangeUser, setRoleChangeUser] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });
      if (searchTerm) params.append("search", searchTerm);

      const res = await axios.get(`${API}/admin/users?${params}`, {
        withCredentials: true
      });
      setUsers(res.data.users || []);
      setPagination(prev => ({
        ...prev,
        total: res.data.total || 0
      }));
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const viewUserProfile = async (user) => {
    setSelectedUser(user);
    setLoadingProfile(true);
    try {
      const res = await axios.get(`${API}/admin/users/${user.user_id}/profile`, {
        withCredentials: true
      });
      setUserProfile(res.data);
    } catch (err) {
      setUserProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleRoleChange = async () => {
    if (!roleChangeUser) return;
    setUpdatingRole(true);
    try {
      const newIsAdmin = !roleChangeUser.is_admin;
      await axios.put(
        `${API}/admin/users/${roleChangeUser.user_id}/role`,
        { is_admin: newIsAdmin },
        { withCredentials: true }
      );
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.user_id === roleChangeUser.user_id ? { ...u, is_admin: newIsAdmin } : u
      ));
      
      toast.success(`Admin privileges ${newIsAdmin ? 'granted to' : 'revoked from'} ${roleChangeUser.name || roleChangeUser.email}`);
      setRoleChangeUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    } finally {
      setUpdatingRole(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="space-y-6" data-testid="admin-users-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                User Management
              </CardTitle>
              <CardDescription>View all registered users and their profile completion status</CardDescription>
            </div>
            <Button variant="outline" onClick={fetchUsers} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-stone-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-stone-900">{pagination.total}</p>
                <p className="text-sm text-stone-600">Total Users</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-700">
                  {users.filter(u => u.profile_completed).length}
                </p>
                <p className="text-sm text-green-600">Profiles Complete</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-700">
                  {users.filter(u => !u.profile_completed).length}
                </p>
                <p className="text-sm text-yellow-600">Profiles Incomplete</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-700">
                  {users.filter(u => u.is_admin).length}
                </p>
                <p className="text-sm text-amber-600">Admins</p>
              </CardContent>
            </Card>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-stone-700 mb-2">No Users Found</h3>
              <p className="text-stone-500">
                {searchTerm ? "Try a different search term" : "No registered users yet"}
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="w-[150px]">Profile Status</TableHead>
                    <TableHead className="w-[100px]">Role</TableHead>
                    <TableHead className="w-[120px]">Joined</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id} className="hover:bg-stone-50" data-testid={`user-row-${user.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            {user.picture ? (
                              <AvatarImage src={user.picture} alt={user.name} />
                            ) : null}
                            <AvatarFallback className="bg-amber-100 text-amber-700">
                              {user.name?.charAt(0)?.toUpperCase() || "U"}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{user.name || "—"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-stone-600">{user.email}</TableCell>
                      <TableCell>
                        {user.profile_completed ? (
                          <Badge className="bg-green-100 text-green-700 flex items-center gap-1 w-fit">
                            <CheckCircle className="w-3 h-3" /> Complete
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-700 flex items-center gap-1 w-fit">
                            <AlertCircle className="w-3 h-3" /> Incomplete
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.is_admin ? (
                          <Badge className="bg-amber-100 text-amber-700 cursor-pointer" onClick={() => user.user_id !== currentUser?.user_id && setRoleChangeUser(user)}>
                            <Shield className="w-3 h-3 mr-1" /> Admin
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="cursor-pointer" onClick={() => setRoleChangeUser(user)}>User</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-stone-600 text-sm">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewUserProfile(user)}
                            data-testid={`view-user-${user.user_id}`}
                            title="View Profile"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {user.user_id !== currentUser?.user_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRoleChangeUser(user)}
                              data-testid={`toggle-admin-${user.user_id}`}
                              title={user.is_admin ? "Revoke Admin" : "Make Admin"}
                              className={user.is_admin ? "text-red-600 hover:text-red-700" : "text-amber-600 hover:text-amber-700"}
                            >
                              {user.is_admin ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-stone-600">
                Page {pagination.page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Profile Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setUserProfile(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="user-profile-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-amber-600" />
              User Profile Details
            </DialogTitle>
          </DialogHeader>

          {loadingProfile ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
            </div>
          ) : selectedUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg">
                <Avatar className="h-16 w-16">
                  {selectedUser.picture ? (
                    <AvatarImage src={selectedUser.picture} alt={selectedUser.name} />
                  ) : null}
                  <AvatarFallback className="bg-amber-100 text-amber-700 text-xl">
                    {selectedUser.name?.charAt(0)?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-lg">{selectedUser.name || "—"}</h3>
                  <p className="text-stone-600 flex items-center gap-1">
                    <Mail className="w-4 h-4" /> {selectedUser.email}
                  </p>
                  <p className="text-sm text-stone-500 flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> Joined {formatDate(selectedUser.created_at)}
                  </p>
                </div>
              </div>

              {/* Profile Data */}
              {userProfile ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500 uppercase mb-1">Location</p>
                      <p className="font-medium flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-amber-600" />
                        {userProfile.location || "Not specified"}
                      </p>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500 uppercase mb-1">Pet Type</p>
                      <p className="font-medium capitalize">{userProfile.pet_type || "Not specified"}</p>
                    </div>
                  </div>

                  {userProfile.bio && (
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500 uppercase mb-1">Bio</p>
                      <p className="text-stone-700">{userProfile.bio}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <Cat className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-amber-700">{userProfile.num_cats || 0}</p>
                      <p className="text-xs text-amber-600">Cats</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <Dog className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-blue-700">{userProfile.num_dogs || 0}</p>
                      <p className="text-xs text-blue-600">Dogs</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <Users className="w-5 h-5 text-green-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-green-700">{userProfile.num_other_pets || 0}</p>
                      <p className="text-xs text-green-600">Other</p>
                    </div>
                  </div>

                  {userProfile.other_pets_description && (
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500 uppercase mb-1">Other Pets</p>
                      <p className="text-stone-700">{userProfile.other_pets_description}</p>
                    </div>
                  )}

                  {userProfile.interested_in_house_sitting && (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Interested in House Sitting
                    </Badge>
                  )}

                  {userProfile.pet_photo_base64 && (
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500 uppercase mb-2">Pet Photo</p>
                      <img 
                        src={`data:${userProfile.pet_photo_mime_type || 'image/jpeg'};base64,${userProfile.pet_photo_base64}`}
                        alt="User's pet"
                        className="w-full max-w-[200px] rounded-lg"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 text-stone-500">
                  <AlertCircle className="w-12 h-12 text-stone-300 mx-auto mb-2" />
                  <p>Profile not completed yet</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Role Change Confirmation Dialog */}
      <Dialog open={!!roleChangeUser} onOpenChange={() => setRoleChangeUser(null)}>
        <DialogContent className="sm:max-w-md" data-testid="role-change-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {roleChangeUser?.is_admin ? (
                <><ShieldOff className="w-5 h-5 text-red-600" /> Revoke Admin Privileges</>
              ) : (
                <><Shield className="w-5 h-5 text-amber-600" /> Grant Admin Privileges</>
              )}
            </DialogTitle>
            <DialogDescription>
              {roleChangeUser?.is_admin 
                ? `Are you sure you want to remove admin privileges from ${roleChangeUser?.name || roleChangeUser?.email}? They will lose access to the admin panel.`
                : `Are you sure you want to grant admin privileges to ${roleChangeUser?.name || roleChangeUser?.email}? They will have full access to the admin panel.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg">
              <Avatar className="h-12 w-12">
                {roleChangeUser?.picture ? (
                  <AvatarImage src={roleChangeUser.picture} alt={roleChangeUser.name} />
                ) : null}
                <AvatarFallback className="bg-amber-100 text-amber-700 text-lg">
                  {roleChangeUser?.name?.charAt(0)?.toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold">{roleChangeUser?.name || "—"}</p>
                <p className="text-sm text-stone-600">{roleChangeUser?.email}</p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRoleChangeUser(null)} disabled={updatingRole}>
              Cancel
            </Button>
            <Button 
              onClick={handleRoleChange}
              disabled={updatingRole}
              className={roleChangeUser?.is_admin ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}
              data-testid="confirm-role-change-btn"
            >
              {updatingRole ? "Updating..." : roleChangeUser?.is_admin ? "Revoke Admin" : "Grant Admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersTab;
